import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import {
    AXNUIContent,
    AXNUIContentHeader,
    AXNUITheme,
    AXNUIThemeHeader
} from "@iddataweb/idw-common-ui/types";
import { ThemingHeaderValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingHeaderCustomSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingHeaderValidationErrors
    updateAXNUIContentHeaderFieldValue: (key: keyof AXNUIContentHeader, value: string) => void;
    updateAXNUIThemeHeaderFieldValue: (key: keyof AXNUIThemeHeader, value: string | boolean) => void;
};

export const ThemingHeaderCustomSection = (props: ThemingHeaderCustomSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIContentHeaderFieldValue,
        updateAXNUIThemeHeaderFieldValue
    } = props;

    const handleUseCustomHtmlAndCssChange = (newValue: string) => {
        updateAXNUIThemeHeaderFieldValue("useHtmlCss", isStringTrue(newValue));
    };

    const handleResetUseCustomHtmlAndCss = () => {
        updateAXNUIThemeHeaderFieldValue("useHtmlCss", inheritedAXNUITheme.header.useHtmlCss);
    };

    const handleCustomHtmlChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentHeaderFieldValue("customHtmlHeader", event.target.value);
    };

    const handleResetCustomHtml = () => {
        updateAXNUIContentHeaderFieldValue("customHtmlHeader", inheritedAXNUIContent.header.customHtmlHeader);
    };

    const handleCustomCssChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentHeaderFieldValue("customCssHeader", event.target.value);
    };

    const handleResetCustomCss = () => {
        updateAXNUIContentHeaderFieldValue("customCssHeader", inheritedAXNUIContent.header.customCssHeader);
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.header.useHtmlCss}
                currentThemingPropertyValue={axnuiTheme.header.useHtmlCss}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-header-use-custom-html-css-radio-input`}
                    automationId={`theming-header-use-custom-html-css-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.useCustomHtmlAndCss.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleUseCustomHtmlAndCssChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiTheme.header.useHtmlCss ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.useCustomHtmlAndCss.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetUseCustomHtmlAndCss}
                />
            </ThemingFormGroupWrapper>
            {axnuiTheme.header.useHtmlCss && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.header.customHtmlHeader}
                        currentThemingPropertyValue={axnuiContent.header.customHtmlHeader}
                        pageLevel={pageLevel}
                    >
                        <TextareaFormGroup
                            controlId={"theming-header-custom-html-input"}
                            automationId={"theming-header-custom-html-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customHtml.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customHtml.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCustomHtmlChange}
                            value={axnuiContent.header.customHtmlHeader || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customHtml.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCustomHtml}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.customHtmlError}

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.header.customCssHeader}
                        currentThemingPropertyValue={axnuiContent.header.customCssHeader}
                        pageLevel={pageLevel}
                    >
                        <TextareaFormGroup
                            controlId={"theming-header-custom-css-input"}
                            automationId={"theming-header-custom-css-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customCss.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customCss.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCustomCssChange}
                            value={axnuiContent.header.customCssHeader || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.inputs.customCss.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCustomCss}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.customCssError}
                </>
            )}
        </div>
    );
};